import { Search, X } from "lucide-react";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { getPaperPairs } from "@/services/controllers/trade";
import { useAppSelector } from "@/services/hooks/redux.hooks";

import LoadingSpinner from "../loading-spinner/loading-spinner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ScrollArea } from "../ui/scroll-area";
import { Separator } from "../ui/separator";

const PairInput: FC<{
  onClick: any;
  disabled?: boolean;
  coin: string;
  defaultItem?: any;
}> = ({ onClick, disabled, coin, defaultItem }) => {
  const { providers } = useAppSelector((state) => state.exchangeSwitcher);
  const ref = useRef<any>();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [focused, setFocused] = useState<boolean>();
  const [pairs, setPairs] = useState<any[]>([]);
  const [pair, setPair] = useState<any>(defaultItem);

  useEffect(() => {
    if (defaultItem?.name != null) {
      ref.current.value = `Binance • ${defaultItem.name}`;
      setPair(defaultItem);
    }
  }, [defaultItem]);

  const fetchData = useCallback(
    (search: string) => {
      getPaperPairs(search)
        .then((response) => {
          const arr: any[] = [];
          Object.keys(response.data.data).forEach((exchangeId) => {
            arr.push({
              exchange: providers.filter(
                (e) => e.id === parseFloat(exchangeId),
              )[0].name,
              data: response.data.data?.[exchangeId],
            });
          });
          setPairs(arr);
        })
        .finally(() => setIsLoading(false));
    },
    [providers],
  );

  useEffect(() => {
    if (isLoading && coin != null && coin !== "") {
      fetchData(coin);
    }

    if (coin == null) {
      setIsLoading(true);
      setPair(undefined);
      setPairs([]);
      setFocused(false);
      setSearchTerm("");
      ref.current.value = "";
      if (onClick) onClick(undefined);
    }
  }, [coin, fetchData, isLoading, onClick]);

  const debouncedSearch = useCallback((event: any) => {
    setSearchTerm(event.target.value);
  }, []);

  const customInput = useCallback(
    () => (
      <div
        className="gap-1 flex flex-col items-center justify-center w-full h-fit"
        id="exchange-pair"
      >
        <div className="border-[1px] shadow-sm w-full h-[40px] rounded-sm ps-2 pe-4 gap-2 relative flex items-center justify-center">
          <Input
            ref={ref}
            className={twMerge(
              pair?.name ? "font-bold" : "",
              "text-sm  flex-1 pl-0 focus-visible:ring-0 border-0 shadow-none",
            )}
            onChange={debouncedSearch}
            placeholder="Select exchange-pair"
            type="text"
            onFocus={() => setFocused(true)}
            onBlur={() => {
              if (searchTerm !== "") {
                return;
              }
              setTimeout(() => setFocused(false), 200);
            }}
            disabled={disabled || pair?.name != null}
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          {searchTerm !== "" || ref?.current?.value !== "" ? (
            <X
              className="w-4 h-auto absolute right-0 me-10 top-[30%] cursor-pointer"
              onClick={() => {
                setFocused(false);
                setSearchTerm("");
                ref.current.value = "";
                setPair(undefined);
                if (onClick) onClick(undefined);
              }}
            />
          ) : (
            <></>
          )}
        </div>
      </div>
    ),
    [pair?.name, debouncedSearch, disabled, searchTerm, onClick],
  );

  return (
    <div className="w-full relative flex flex-col items-center justify-center">
      {customInput()}

      {focused ? (
        <div className="max-h-[250px] bg-sidebar  border w-full  shadow-sm top-12 absolute rounded-sm z-10 ">
          <ScrollArea className="h-[250px] p-1 pr-3 w-full">
            {isLoading ? (
              <LoadingSpinner />
            ) : pairs.length ? (
              pairs?.map((pair) => {
                const filteredData = pair.data.filter((item: any) =>
                  item.name.toLowerCase().includes(searchTerm.toLowerCase()),
                );
                return (
                  <div
                    className="w-full flex flex-col items-center justify-center"
                    key={pair.exchange}
                  >
                    <p className="text-sm font-bold my-2 text-start w-full">
                      {pair.exchange}
                    </p>
                    <Separator />
                    {!filteredData.length ? (
                      <span className="w-full text-left text-sm ps-2">
                        No pairs with this keyword!
                      </span>
                    ) : (
                      filteredData.map((info: any) => {
                        return (
                          <Button
                            key={info.id}
                            variant="ghost"
                            className="min-h-[44px] py-1 px-2 text-start w-full"
                            onClick={() => {
                              onClick(info);
                              ref.current.value = `${pair.exchange} • ${info.name}`;
                              setPair(info);
                              setFocused(false);
                            }}
                          >
                            <p className="font-medium text-sm text-start w-full">
                              {info.name}
                            </p>
                          </Button>
                        );
                      })
                    )}
                  </div>
                );
              })
            ) : (
              <span className="w-full mx-4 text-center">
                No pairs with this keyword!
                <br />
                <br />
                <span>{searchTerm}</span>
              </span>
            )}
          </ScrollArea>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default PairInput;
